export const SCOPES = ['journey', 'session', 'global', 'system'];
/** Purpose: normalize legacy/unknown variable scopes. Why: old `user` variables remain compatible while all consumers use the supported scope names. */
export const variableScope = (scope) => scope === 'user' ? 'session' : SCOPES.includes(scope) ? scope : 'journey';
/** Purpose: format a variable's canonical scoped reference. Why: the flow editor and runtime need an unambiguous shared name for variable lookup. */
export const variableReference = (variable) => `${variableScope(variable.scope)}.${variable.name}`;
/** Purpose: parse and validate a variable default from its editor text. Why: saved defaults must match their declared type before a flow can use them. */
export function parseDefault(type, text) {
  if (type === 'string') return text;
  if (type === 'date') {
    if (!text || Number.isNaN(Date.parse(text))) throw new Error('Enter a valid date.');
    return text;
  }
  if (type === 'number') {
    if (!text.trim() || !Number.isFinite(Number(text))) throw new Error('Enter a valid number.');
    return Number(text);
  }
  if (type === 'boolean') {
    if (!['true', 'false'].includes(text)) throw new Error('Choose true or false.');
    return text === 'true';
  }
  let parsed;
  try { parsed = JSON.parse(text); } catch { throw new Error('Enter valid JSON.'); }
  if (type === 'array' && !Array.isArray(parsed)) throw new Error('Enter a JSON array.');
  if (type === 'object' && (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))) throw new Error('Enter a JSON object.');
  return parsed;
}
/** Purpose: locate nodes and connections that reference a variable. Why: the editor can show impact before a user renames or removes that variable. */
export function variableUsages(variable, nodes = [], edges = []) {
  const escaped = variable.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const scope = variableScope(variable.scope);
  const aliases = scope === 'journey' ? '(?:journey|local|node)' : scope === 'session' ? '(?:session|user)' : scope;
  const pattern = new RegExp(`(?:${aliases}\\.|variables\\.|\\{\\{\\{?\\s*)${escaped}(?![A-Za-z0-9_])`);
  // Purpose: recursively inspect nested node/edge configuration. Why: variables may be embedded in choices, API payloads, or other structured settings.
  const visit = (value) => {
    if (typeof value === 'string') return value === variable.name || pattern.test(value);
    if (!value || typeof value !== 'object') return false;
    return Object.values(value).some(visit);
  };
  return [...nodes.filter((node) => visit(node.data?.config || node.config)).map((node) => node.data?.title || node.name || node.id),
    ...edges.filter((edge) => visit(edge.data)).map((edge) => `Connection ${edge.source} → ${edge.target}`)];
}
